import {useMemo} from "react";
import {useQuery} from "@tanstack/react-query";

import {actionableBriefingsForShift} from "../lib/inbox-status";
import {BRIEFING_BOARD_INBOX_PAGE_SIZE, briefingBoardsService} from "../services";

type UsePendingBriefingsParams = {
  shiftId?: number | null;
  enabled?: boolean;
};

const GATE_PAGE = {limit: BRIEFING_BOARD_INBOX_PAGE_SIZE, offset: 0};

export default function usePendingBriefings({
  shiftId,
  enabled = true,
}: UsePendingBriefingsParams = {}) {
  const pendingParams = {
    shift_id: shiftId ?? undefined,
    view: "gate" as const,
    page: GATE_PAGE,
  };

  const pendingQuery = useQuery({
    queryKey: ["briefingboard-pending", pendingParams],
    queryFn: () => briefingBoardsService.getPending(pendingParams),
    enabled: enabled && shiftId != null,
    retry: false,
    refetchOnWindowFocus: true,
    networkMode: "offlineFirst",
  });

  const pendingBoards =
    pendingQuery.isSuccess && pendingQuery.data ? pendingQuery.data.data : [];

  const shouldTryInboxFallback =
    Boolean(enabled && shiftId != null) &&
    pendingQuery.isSuccess &&
    !pendingQuery.isFetching &&
    pendingBoards.length === 0;

  const inboxParams = {unread: true, page: GATE_PAGE};

  const inboxFallbackQuery = useQuery({
    queryKey: ["briefingboard-inbox", inboxParams, "gate-fallback", shiftId],
    queryFn: () => briefingBoardsService.getInbox(inboxParams),
    enabled: shouldTryInboxFallback,
    retry: false,
    refetchOnWindowFocus: true,
    networkMode: "offlineFirst",
  });

  const briefingBoards = useMemo(() => {
    if (pendingBoards.length > 0) {
      return pendingBoards;
    }
    if (
      !shouldTryInboxFallback ||
      !inboxFallbackQuery.isSuccess ||
      !inboxFallbackQuery.data ||
      shiftId == null
    ) {
      return [];
    }
    return actionableBriefingsForShift(inboxFallbackQuery.data.data, shiftId);
  }, [
    pendingBoards,
    shouldTryInboxFallback,
    inboxFallbackQuery.isSuccess,
    inboxFallbackQuery.data,
    shiftId,
  ]);

  const isPending =
    (Boolean(enabled && shiftId != null) && pendingQuery.isPending) ||
    (shouldTryInboxFallback && inboxFallbackQuery.isPending);

  const totalRecords =
    pendingBoards.length > 0
      ? (pendingQuery.data?.meta?.total ?? pendingBoards.length)
      : briefingBoards.length;

  return {
    briefingBoards,
    totalRecords,
    hasPending: briefingBoards.length > 0,
    isPending,
    isFetching: pendingQuery.isFetching || inboxFallbackQuery.isFetching,
    isError: pendingQuery.isError && inboxFallbackQuery.isError,
    refetch: async () => {
      await Promise.all([
        pendingQuery.refetch(),
        shouldTryInboxFallback ? inboxFallbackQuery.refetch() : Promise.resolve(),
      ]);
    },
  };
}
